import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, CreditCard, Loader2, DollarSign, Plus } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { useTimeFormatter } from "@/lib/timeFormat";

let _stripePromise = null;
const getStripe = async () => {
  if (_stripePromise) return _stripePromise;
  const { data } = await base44.functions.invoke("stripePaymentMethods", { action: "get_publishable_key" });
  _stripePromise = loadStripe(data.publishableKey);
  return _stripePromise;
};

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function checkConflict(sessions, trainerId, date, durationMinutes, excludeId) {
  if (!trainerId || !date) return false;
  const start = new Date(date).getTime();
  const end = start + (durationMinutes || 60) * 60000;
  return sessions.some((s) => {
    if (s.id === excludeId) return false;
    if (s.trainer !== trainerId) return false;
    if (s.status === "cancelled") return false;
    const sStart = new Date(s.date).getTime();
    const sEnd = sStart + (s.duration_minutes || 60) * 60000;
    return start < sEnd && end > sStart;
  });
}

function checkOutsideHours(trainer, date, durationMinutes) {
  if (!trainer || !date) return false;
  const workHours = trainer.work_hours;
  if (!workHours) return false;
  const dt = new Date(date);
  const dayName = DAYS[dt.getDay()];
  const dayConfig = workHours[dayName];
  if (!dayConfig || !dayConfig.enabled) return true;
  const sessionStart = dt.getHours() * 60 + dt.getMinutes();
  const sessionEnd = sessionStart + (durationMinutes || 60);
  const [startH, startM] = dayConfig.start.split(":").map(Number);
  const [endH, endM] = dayConfig.end.split(":").map(Number);
  return sessionStart < (startH * 60 + startM) || sessionEnd > (endH * 60 + endM);
}

const HOUR_OPTIONS = [1, 2, 3, 4];

export default function SessionForm({ open, onOpenChange, onSubmit, initialData, session }) {
  const fmtTime = useTimeFormatter();
  const source = session || initialData;
  const queryClient = useQueryClient();

  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers-active"],
    queryFn: () => base44.entities.Trainer.filter({ is_active: true }),
  });

  // Map trainer user_id back to trainer entity id for the dropdown
  const resolveTrainerId = (src) => {
    if (!src?.trainer) return "";
    // If it matches an entity id directly, use it
    const byId = trainers.find(t => t.id === src.trainer);
    if (byId) return byId.id;
    // Otherwise it's stored as user_id — find by user_id
    const byUserId = trainers.find(t => t.user_id === src.trainer);
    return byUserId ? byUserId.id : "";
  };

  const buildForm = (src) => src ? {
    title: src.title || "",
    date: src.date || "",
    duration_minutes: src.duration_minutes || 60,
    session_type: src.session_type || "personal_training",
    trainer: resolveTrainerId(src),
    notes: src.notes || "",
    status: src.status || "upcoming",
  } : {
    title: "",
    date: "",
    duration_minutes: 60,
    session_type: "personal_training",
    trainer: "",
    notes: "",
    status: "upcoming",
  };

  const [form, setForm] = useState(buildForm(source));

  useEffect(() => {
    if (open) {
      setForm(buildForm(source));
      setConflict(false);
      setOutsideHours(false);
      setPmError(null);
    }
  }, [open, session, initialData, trainers]);

  const [conflict, setConflict] = useState(false);
  const [outsideHours, setOutsideHours] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPmId, setSelectedPmId] = useState(null);
  const [loadingPm, setLoadingPm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pmError, setPmError] = useState(null);

  const { data: sessions = [] } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => base44.entities.TrainingSession.list("-date", 200),
  });

  useEffect(() => {
    if (!open) return;
    setLoadingPm(true);
    base44.functions.invoke("stripePaymentMethods", { action: "list" })
      .then(async ({ data }) => {
        const pms = data.paymentMethods || [];
        setPaymentMethods(pms);
        // Pre-select default
        const user = await base44.auth.me();
        const defId = user?.default_payment_method_id;
        if (defId && pms.find(p => p.id === defId)) setSelectedPmId(defId);
        else if (pms.length > 0) setSelectedPmId(pms[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingPm(false));
  }, [open]);

  const validate = (updated) => {
    const trainer = trainers.find((t) => t.id === updated.trainer);
    setConflict(checkConflict(sessions, updated.trainer, updated.date, updated.duration_minutes, source?.id));
    setOutsideHours(checkOutsideHours(trainer, updated.date, updated.duration_minutes));
  };

  const set = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    validate(updated);
  };

  const selectedTrainer = trainers.find((t) => t.id === form.trainer);
  const hasWorkHours = selectedTrainer?.work_hours && Object.values(selectedTrainer.work_hours).some(d => d.enabled);
  const hours = (form.duration_minutes || 60) / 60;
  const rate = selectedTrainer?.hourly_rate || 0;
  const totalPrice = rate * hours;
  const needsPayment = rate > 0 && !source; // only on new bookings with a paid trainer

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (conflict || outsideHours) return;
    if (needsPayment && !selectedPmId) {
      setPmError("Please select or add a payment method.");
      return;
    }
    setPmError(null);
    setSubmitting(true);
    try {
      if (source) {
        // Reschedule: only update scheduling fields, preserve trainer/client/payment as-is
        onSubmit({
          title: form.title,
          date: form.date,
          duration_minutes: form.duration_minutes,
          session_type: form.session_type,
          notes: form.notes,
        });
      } else if (needsPayment && selectedPmId) {
        const sessionData = { ...form, trainer: selectedTrainer?.user_id };
        const { data } = await base44.functions.invoke("stripeSessionCheckout", {
          sessionData,
          trainerId: selectedTrainer.id,
          trainerUserId: selectedTrainer.user_id,
          trainerName: selectedTrainer.name,
          hourlyRate: rate,
          durationHours: hours,
          paymentMethodId: selectedPmId,
        });
        queryClient.invalidateQueries({ queryKey: ["sessions"] });
        onSubmit(data.session);
      } else {
        const user = await base44.auth.me();
        onSubmit({ ...form, trainer: selectedTrainer?.user_id, created_by_id: user.id });
      }
      onOpenChange(false);
    } catch (err) {
      setPmError(err.message || "Payment failed. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{source ? "Edit Session" : "Book Session"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Upper Body Strength" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date & Time</Label>
              <Input type="datetime-local" value={form.date} onChange={(e) => set("date", e.target.value)} required />
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={String(form.duration_minutes)} onValueChange={(v) => set("duration_minutes", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOUR_OPTIONS.map(h => (
                    <SelectItem key={h} value={String(h * 60)}>{h} hour{h > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.session_type} onValueChange={(v) => set("session_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal_training">Personal Training</SelectItem>
                  <SelectItem value="group_class">Group Class</SelectItem>
                  <SelectItem value="open_gym">Open Gym</SelectItem>
                  <SelectItem value="assessment">Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Trainer</Label>
              <Select value={form.trainer || ""} onValueChange={(v) => set("trainer", v)}>
                <SelectTrigger><SelectValue placeholder="Select trainer" /></SelectTrigger>
                <SelectContent>
                  {trainers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}{t.hourly_rate ? ` — $${t.hourly_rate}/hr` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Rate + price summary */}
          {selectedTrainer && rate > 0 && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-3 flex items-center justify-between">
              <div className="text-sm">
                <span className="text-muted-foreground">${rate}/hr × {hours}h</span>
              </div>
              <div className="flex items-center gap-1 font-bold text-primary text-lg">
                <DollarSign className="w-4 h-4" />{totalPrice.toFixed(2)}
              </div>
            </div>
          )}

          {/* Trainer hours hint */}
          {selectedTrainer && hasWorkHours && (
            <div className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2 space-y-0.5">
              <p className="font-medium text-foreground">{selectedTrainer.name}'s availability:</p>
              {Object.entries(selectedTrainer.work_hours)
                .filter(([, d]) => d.enabled)
                .map(([day, d]) => (
                  <p key={day} className="capitalize">{day}: {fmtTime(d.start)} – {fmtTime(d.end)}</p>
                ))}
            </div>
          )}

          {conflict && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              This trainer already has a session at this time.
            </div>
          )}
          {outsideHours && !conflict && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              This trainer is not available at the selected time.
            </div>
          )}

          {/* Payment method section */}
          {needsPayment && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Payment Method</Label>
              {loadingPm ? (
                <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
              ) : paymentMethods.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-muted rounded-lg px-3 py-3 flex items-center justify-between">
                  <span>No payment methods saved</span>
                  <a href="/account" className="text-primary text-xs font-medium flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add card in Account
                  </a>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {paymentMethods.map((pm) => (
                    <label key={pm.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedPmId === pm.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}>
                      <input type="radio" name="pm" value={pm.id} checked={selectedPmId === pm.id} onChange={() => setSelectedPmId(pm.id)} className="accent-primary" />
                      <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm capitalize">{pm.card.brand} •••• {pm.card.last4}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{pm.card.exp_month}/{pm.card.exp_year}</span>
                    </label>
                  ))}
                  <a href="/account" className="text-xs text-primary flex items-center gap-1 pl-1">
                    <Plus className="w-3 h-3" /> Add new card
                  </a>
                </div>
              )}
              {pmError && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {pmError}
                </div>
              )}
            </div>
          )}

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any notes..." rows={2} />
          </div>
          <Button type="submit" className="w-full" disabled={conflict || outsideHours || submitting || (needsPayment && paymentMethods.length === 0)}>
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : needsPayment ? `Pay $${totalPrice.toFixed(2)} & Book` : source ? "Update Session" : "Book Session"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}